/*
 * uShip delivery terms gate (see sections/ud-uship-gate.liquid for what it does and ud-uship-gate.css / ud-mto-gate.css for the look).
 *
 * This is a second copy of the made-to-order gate (ud-mto-gate.js) with its own tag, wording and order detail. Every way into
 * checkout that goes through the page is caught here: the "Check out" button on the cart page, in the cart drawer and in the
 * cart pop-up, and plain links to /checkout. If the cart holds a product with the "uShip Delivery" tag and this cart has not
 * already accepted the current terms version, the pop-up opens and checkout waits for the tick box. The acceptance is saved on
 * the cart (an "attribute"), which Shopify copies onto the order.
 *
 * Both gates listen for the same clicks. The made-to-order gate (loaded first) catches a click, and once it is satisfied it
 * clicks the button again, which this gate then catches. After this gate is satisfied it clicks again too, which the made-to-order
 * gate checks once more (it passes straight away, the cart already holds its acceptance), so this gate stays out of the way for
 * a few seconds (BYPASS_MS) while that happens.
 *
 * It cannot stop someone who types /checkout into the address bar or uses a payment app that skips the cart.
 */
(function () {
  'use strict';

  if (window.__udGateUship) return;
  window.__udGateUship = true;

  var root = document.querySelector('[data-ud-gate-uship]');
  var template = root && root.querySelector('template[data-ud-gate-uship-template]');
  if (!root || !template) return;

  var BYPASS_MS = 6000;
  var html = document.documentElement;
  var tag = (root.getAttribute('data-tag') || 'uShip Delivery').toLowerCase();
  var version = root.getAttribute('data-version') || '1';
  var attrName = root.getAttribute('data-attr') || 'uShip delivery terms accepted';
  var hideExpress = root.getAttribute('data-hide-express') === '1';

  var dialog = null;
  var box = null;
  var goBtn = null;
  var errorEl = null;
  var pendingEl = null;
  var bypassUntil = 0;
  var tagCache = {};
  var warmedAt = 0;

  function jsonFetch(url, options) {
    options = options || {};
    options.credentials = 'same-origin';
    options.cache = 'no-store';
    options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    return fetch(url, options).then(function (response) {
      if (!response.ok) throw new Error(url + ' ' + response.status);
      return response.json();
    });
  }

  function getCart() {
    return jsonFetch('/cart.js?ud=' + Date.now());
  }

  function setAttributes(attributes) {
    return jsonFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  // Does this product carry the uShip tag? Remembered per product for the visit (tags rarely change).
  function hasTag(handle) {
    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) return Promise.resolve(tagCache[handle]);
    try {
      var stored = window.sessionStorage.getItem('udu:' + handle);
      if (stored !== null) {
        tagCache[handle] = stored === '1';
        return Promise.resolve(tagCache[handle]);
      }
    } catch (e) {}
    return jsonFetch('/products/' + encodeURIComponent(handle) + '.js').then(function (product) {
      var yes = (product.tags || []).some(function (t) {
        return String(t).toLowerCase() === tag;
      });
      tagCache[handle] = yes;
      try {
        window.sessionStorage.setItem('udu:' + handle, yes ? '1' : '0');
      } catch (e) {}
      return yes;
    });
  }

  function cartNeedsTerms(cart) {
    var seen = {};
    var handles = [];
    (cart.items || []).forEach(function (item) {
      if (item.handle && !seen[item.handle]) {
        seen[item.handle] = true;
        handles.push(item.handle);
      }
    });
    return Promise.all(handles.map(hasTag)).then(function (flags) {
      return flags.some(Boolean);
    });
  }

  function hasAccepted(cart) {
    var value = cart.attributes && cart.attributes[attrName];
    return typeof value === 'string' && value.indexOf('agreement version ' + version + ')') > -1;
  }

  function stamp() {
    var now = new Date();
    var human;
    try {
      human = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      }).format(now);
    } catch (e) {
      human = now.toString();
    }
    return human + ' / ' + now.toISOString().replace(/\.\d+Z$/, 'Z');
  }

  // ---------- Letting checkout carry on ----------
  function proceed(el) {
    bypassUntil = Date.now() + BYPASS_MS;
    el.classList.remove('udu-checking');
    el.click();
  }

  function check(el) {
    el.classList.add('udu-checking');
    getCart()
      .then(function (cart) {
        return cartNeedsTerms(cart).then(function (needs) {
          if (!needs) {
            // No uShip piece in the cart: an old acceptance should not ride along on an unrelated order.
            if (cart.attributes && cart.attributes[attrName]) {
              var clear = {};
              clear[attrName] = '';
              return setAttributes(clear)
                .catch(function () {})
                .then(function () {
                  proceed(el);
                });
            }
            return proceed(el);
          }
          if (hasAccepted(cart)) return proceed(el);
          return openDialog(el);
        });
      })
      .catch(function () {
        // If anything goes wrong we ask rather than let a uShip order through unchecked.
        openDialog(el);
      });
  }

  // ---------- The pop-up ----------
  function ensureDialog() {
    if (dialog) return dialog;
    dialog = template.content.cloneNode(true).querySelector('dialog');
    document.body.appendChild(dialog);
    box = dialog.querySelector('[data-udu-box]');
    goBtn = dialog.querySelector('[data-udu-go]');
    errorEl = dialog.querySelector('[data-udu-error]');

    box.addEventListener('change', function () {
      goBtn.disabled = !box.checked;
      errorEl.hidden = true;
    });
    goBtn.addEventListener('click', accept);
    dialog.querySelector('[data-udu-cancel]').addEventListener('click', function () {
      dialog.close();
    });
    dialog.addEventListener('close', function () {
      html.classList.remove('udg-lock');
      if (pendingEl) {
        var el = pendingEl;
        pendingEl = null;
        try {
          el.focus();
        } catch (e) {}
      }
    });
    return dialog;
  }

  function openDialog(el) {
    el.classList.remove('udu-checking');
    pendingEl = el;
    var d = ensureDialog();
    box.checked = false;
    goBtn.disabled = true;
    goBtn.removeAttribute('aria-busy');
    errorEl.hidden = true;
    html.classList.add('udg-lock');
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
    d.querySelector('.udg__scroll').scrollTop = 0;
    d.querySelector('.udg__title').focus();
  }

  function accept() {
    if (!box.checked || goBtn.getAttribute('aria-busy') === 'true') return;
    goBtn.setAttribute('aria-busy', 'true');
    errorEl.hidden = true;
    var value = 'Accepted ' + stamp() + ' (agreement version ' + version + ')';
    var attributes = {};
    attributes[attrName] = value;
    setAttributes(attributes)
      .then(function (cart) {
        if (!cart.attributes || cart.attributes[attrName] !== value) throw new Error('not saved');
        var el = pendingEl;
        pendingEl = null;
        dialog.close();
        if (el) proceed(el);
      })
      .catch(function () {
        goBtn.removeAttribute('aria-busy');
        errorEl.hidden = false;
      });
  }

  // ---------- Catching the checkout buttons ----------
  function checkoutTarget(node) {
    if (!node || !node.closest) return null;
    var button = node.closest('button[name="checkout"], input[name="checkout"]');
    if (button) return button;
    var link = node.closest('a[href]');
    if (link && link.origin === window.location.origin && /^\/checkout(\/|$)/.test(link.pathname)) return link;
    return null;
  }

  document.addEventListener(
    'click',
    function (event) {
      if (Date.now() < bypassUntil) return;
      var el = checkoutTarget(event.target);
      if (!el || el.disabled || (dialog && dialog.contains(el))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!el.classList.contains('udu-checking')) check(el);
    },
    true
  );

  // Pressing Enter in a cart field submits the cart form with the checkout button as the submitter.
  document.addEventListener(
    'submit',
    function (event) {
      if (Date.now() < bypassUntil) return;
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!submitter.classList.contains('udu-checking')) check(submitter);
    },
    true
  );

  // Look up the cart's products as soon as the checkout button is touched, so the click itself is quick.
  function warm(event) {
    if (!checkoutTarget(event.target) || Date.now() - warmedAt < 5000) return;
    warmedAt = Date.now();
    getCart()
      .then(cartNeedsTerms)
      .catch(function () {});
  }
  document.addEventListener('pointerover', warm, true);
  document.addEventListener('focusin', warm, true);
  document.addEventListener('touchstart', warm, { capture: true, passive: true });

  window.addEventListener('pageshow', function () {
    bypassUntil = 0;
    document.querySelectorAll('.udu-checking').forEach(function (el) {
      el.classList.remove('udu-checking');
    });
  });

  // ---------- Express checkout buttons on the cart page ----------
  // (Product pages are handled in the section: the tag is known when the page is built.)
  var cartFooter = hideExpress ? document.getElementById('main-cart-footer') : null;
  if (cartFooter) {
    var refreshTimer = 0;
    var refreshExpress = function () {
      getCart()
        .then(cartNeedsTerms)
        .then(function (needs) {
          html.setAttribute('data-ud-uship-cart', needs ? '1' : '0');
        })
        .catch(function () {
          html.setAttribute('data-ud-uship-cart', '1');
        });
    };
    refreshExpress();
    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function () {
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(refreshExpress, 400);
      });
      var items = document.getElementById('main-cart-items');
      if (items) observer.observe(items, { childList: true, subtree: true });
      observer.observe(cartFooter, { childList: true, subtree: true });
    }
  }
})();
